import { IconButton } from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';
import { EngagementPlan } from '@redux/tasks/tasksTypes';
import EngagementPlanDefaultImage from 'assets/images/EngagementPlanDefaultImage.jpg';
import EngagementPlanDefaultImage2 from 'assets/images/EngagementPlanDefaultImage2.png';
import { DropDownMenu } from 'components/DropDownMenu/DropDownMenu';
import { CopyIcon, DotsThreeIcon, TrashIcon } from 'components/Icons';
import { Text } from 'components/Typography';
import { Color } from 'utils/color';
import { countEngagementPlanTasks } from 'utils/taskUtils';
import style from './PlansListItem.scss';

type Props = {
  plan: EngagementPlan;
  isSelected: boolean;
  disabled?: boolean;
  onClick: (plan: EngagementPlan) => void;
  onDuplicateClick?: (plan: EngagementPlan) => void;
  onUpdateClick?: (plan: EngagementPlan) => void;
  onDisablePlan?: (plan: EngagementPlan) => void;
};

export const PlansListItem: React.FC<Props> = ({ plan, isSelected, disabled = false, onClick, onDuplicateClick, onDisablePlan }) => {
  const imageLink = plan.organizationIds?.length ? plan.imageSource || EngagementPlanDefaultImage2 : EngagementPlanDefaultImage;
  const tasksSpecification = countEngagementPlanTasks(plan);
  let description = `${plan.durationDays}-day${plan.durationDays === 1 ? '' : 's'} plan`;

  if (tasksSpecification.total) {
    if (tasksSpecification.recurring) {
      description += ` · ${tasksSpecification.recurring} recurring task${tasksSpecification.recurring === 1 ? '' : 's'}`;
    } else {
      description += ` · ${tasksSpecification.noRepeat} total task${tasksSpecification.noRepeat === 1 ? '' : 's'}`;
    }
  }

  return (
    <div
      className={classNames(style.container, {
        [style.selectedPlan]: isSelected,
        [style.active]: !disabled
      })}
      onClick={() => !disabled && onClick(plan)}
    >
      <div className={style.image} style={{ backgroundImage: `url(${imageLink})` }} />
      <div className={style.header}>
        <Text variant="md" weight={500}>
          {plan.title}
        </Text>
        <div>
          <Text variant="sm" color={Color.GRAY} className={style.metaText}>
            {disabled ? 'Coming soon... ' : description}
          </Text>
        </div>
      </div>
      {(!!plan.canDuplicate || !!onDisablePlan) && (
        <div className={style.meatballButton}>
          <DropDownMenu
            customMenuElement={(onMenuClick) => (
              <IconButton onClick={onMenuClick} size="small">
                <DotsThreeIcon />
              </IconButton>
            )}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            menuItems={[
              !!plan.canDuplicate && {
                customMenuItem: () => (
                  <div className={style.menuOption}>
                    <div className={style.iconWrapper}>
                      <CopyIcon width={16} height={16} />
                    </div>
                    <div className={style.menuOptionTextWrapper}>
                      <Text variant="md">Duplicate plan</Text>
                      <Text variant="sm" color={Color.GRAY_700} className={style.optionSubtext}>
                        Use this plan’s content to create a new plan and customize tasks as needed.
                      </Text>
                    </div>
                  </div>
                ),
                onClick: () => onDuplicateClick?.(plan)
              },
              !!onDisablePlan && {
                text: 'Delete plan',
                textClassName: style.redText,
                onClick: () => onDisablePlan(plan),
                icon: () => <TrashIcon width={16} height={16} color={Color.RED_600} />
              }
            ]}
          />
        </div>
      )}
    </div>
  );
};
